import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  ActivityIndicator,
  TextInput,
  Modal,
  Pressable,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import { hasSuperUserAccess, isSuperUser } from '../utils/sessionUser';
import { fetchVisibleStudents } from '../utils/fetchVisibleStudents';

const LIGHT_CREAM = '#faf8f4';
const EMAIL_FORMAT_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PAGE_TITLE = 'Create/Edit Users';

function isValidEmail(value) {
  return EMAIL_FORMAT_RE.test(String(value || '').trim());
}

function normalizeEmail(value) {
  return String(value || '').trim().toLowerCase();
}

function studentDisplayName(student) {
  if (!student) return 'Select student';
  const name = String(student.student_name || '').trim();
  if (name) return name;
  return String(student.username || 'Student');
}

export default function CreateUserPage({ navigation, route }) {
  const theme = useTheme();
  const [currentUser, setCurrentUser] = useState(route?.params?.user || null);
  const [builderTab, setBuilderTab] = useState('create');

  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [isHiddenUser, setIsHiddenUser] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [editEmail, setEditEmail] = useState('');
  const [originalEmail, setOriginalEmail] = useState('');

  useEffect(() => {
    const uid = route?.params?.userId || 1;
    fetch(`${getApiUrl()}/api/users/${uid}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => data && setCurrentUser(data))
      .catch(() => {});
  }, [route?.params?.userId]);

  const loadStudents = useCallback(async () => {
    if (!currentUser?.id) return;
    setLoadingStudents(true);
    try {
      const { students: rows } = await fetchVisibleStudents({ requester: currentUser });
      setStudents(Array.isArray(rows) ? rows : []);
    } catch {
      setStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser?.id && builderTab === 'edit') {
      void loadStudents();
    }
  }, [currentUser?.id, builderTab, loadStudents]);

  const trimmedFirstName = firstName.trim();
  const trimmedEmail = email.trim();
  const emailValid = isValidEmail(trimmedEmail);
  const canCreate = Boolean(trimmedFirstName && emailValid && !saving);

  const selectedStudent = useMemo(
    () =>
      students.find((s) => Number(s.id) === Number(selectedStudentId)) ?? null,
    [students, selectedStudentId]
  );

  const trimmedEditEmail = editEmail.trim();
  const editEmailValid = isValidEmail(trimmedEditEmail);
  const editEmailChanged =
    normalizeEmail(trimmedEditEmail) !== normalizeEmail(originalEmail);
  const canUpdateEdit = Boolean(
    selectedStudentId != null && editEmailValid && editEmailChanged && !saving
  );

  const styles = useMemo(() => getStyles(theme), [theme]);
  const allowed = hasSuperUserAccess(currentUser);
  const showHiddenCheckbox = isSuperUser(currentUser);

  const clearCreateForm = () => {
    setFirstName('');
    setEmail('');
    setIsHiddenUser(false);
  };

  const clearEditForm = () => {
    setSelectedStudentId(null);
    setEditEmail('');
    setOriginalEmail('');
    setShowStudentDropdown(false);
  };

  const handleReset = () => {
    if (builderTab === 'create') {
      clearCreateForm();
    } else {
      clearEditForm();
    }
    setStatusMessage(null);
  };

  const switchBuilderTab = (tab) => {
    if (saving || tab === builderTab) return;
    setBuilderTab(tab);
    setStatusMessage(null);
    if (tab === 'create') {
      clearEditForm();
    } else {
      clearCreateForm();
    }
  };

  const handleSelectStudent = (student) => {
    const studentId = Number(student.id);
    setSelectedStudentId(studentId);
    setShowStudentDropdown(false);
    setStatusMessage(null);

    const listEmail = String(student.email || '').trim();
    setEditEmail(listEmail);
    setOriginalEmail(listEmail);

    if (!currentUser?.id || Number.isNaN(studentId)) return;

    void fetch(
      `${getApiUrl()}/api/users/${encodeURIComponent(String(studentId))}?requester_id=${encodeURIComponent(String(currentUser.id))}`
    )
      .then((res) => (res.ok ? res.json() : null))
      .then((full) => {
        if (!full || Number(full.id) !== studentId) return;
        const loaded = String(full.email || '').trim();
        setOriginalEmail(loaded);
        setEditEmail((prev) => {
          if (normalizeEmail(prev) === normalizeEmail(listEmail)) {
            return loaded;
          }
          return prev;
        });
      })
      .catch(() => {});
  };

  const handleCreate = async () => {
    if (!canCreate || !currentUser?.id) return;
    setSaving(true);
    setStatusMessage(null);
    try {
      const res = await fetch(`${getApiUrl()}/api/students/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requester_id: currentUser.id,
          student_name: trimmedFirstName,
          email: trimmedEmail,
          isHiddenUser: isHiddenUser,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setStatusMessage({ type: 'error', text: data.error || 'Failed to create user' });
        return;
      }
      clearCreateForm();
      setStatusMessage({ type: 'success', text: 'User Created.' });
    } catch (e) {
      setStatusMessage({
        type: 'error',
        text: e?.message || 'Failed to create user',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateEmail = async () => {
    if (!canUpdateEdit || !currentUser?.id || !selectedStudentId) return;
    setSaving(true);
    setStatusMessage(null);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/students/${encodeURIComponent(String(selectedStudentId))}/update-email`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            requester_id: currentUser.id,
            email: trimmedEditEmail,
          }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setStatusMessage({ type: 'error', text: data.error || 'Failed to update email' });
        return;
      }

      const updatedEmail = String(data.student?.email || trimmedEditEmail).trim();
      setStudents((prev) =>
        prev.map((s) =>
          Number(s.id) === Number(selectedStudentId) ? { ...s, email: updatedEmail } : s
        )
      );
      setEditEmail(updatedEmail);
      setOriginalEmail(updatedEmail);
      setStatusMessage({
        type: 'success',
        text: data.message || 'Email updated. A password reset link has been sent.',
      });
    } catch (e) {
      setStatusMessage({
        type: 'error',
        text: e?.message || 'Failed to update email',
      });
    } finally {
      setSaving(false);
    }
  };

  const renderFeedbackToast = () => {
    if (!statusMessage) return null;
    return (
      <View
        style={[
          styles.feedbackToast,
          statusMessage.type === 'success'
            ? styles.feedbackToastSuccess
            : styles.feedbackToastError,
        ]}
        {...(Platform.OS === 'web'
          ? {
              dataSet: {
                createUserFeedbackToast: 'true',
                createUserFeedbackToastType: statusMessage.type,
              },
            }
          : {})}
      >
        <Text
          style={styles.feedbackToastMessage}
          {...(Platform.OS === 'web'
            ? { dataSet: { createUserFeedbackToastText: 'true' } }
            : {})}
        >
          {statusMessage.text}
        </Text>
        <TouchableOpacity
          style={styles.feedbackToastClose}
          onPress={() => setStatusMessage(null)}
          {...(Platform.OS === 'web'
            ? { dataSet: { createUserFeedbackToastClose: 'true' } }
            : {})}
        >
          <Text style={styles.feedbackToastCloseText}>✕</Text>
        </TouchableOpacity>
      </View>
    );
  };

  const renderCreateTab = () => (
    <>
      <View style={styles.fieldContainer}>
        <Text style={styles.label}>First Name</Text>
        <TextInput
          style={styles.input}
          value={firstName}
          onChangeText={setFirstName}
          placeholder="First name"
          placeholderTextColor={theme.isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)'}
          autoCapitalize="words"
          autoCorrect={false}
          editable={!saving}
          {...(Platform.OS === 'web' ? { dataSet: { createUserFirstNameInput: 'true' } } : {})}
        />
      </View>

      <View style={styles.fieldContainer}>
        <Text style={styles.label}>Email</Text>
        <TextInput
          style={[styles.input, trimmedEmail.length > 0 && !emailValid && styles.inputInvalid]}
          value={email}
          onChangeText={setEmail}
          placeholder="name@example.com"
          placeholderTextColor={theme.isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)'}
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
          editable={!saving}
          {...(Platform.OS === 'web' ? { dataSet: { createUserEmailInput: 'true' } } : {})}
        />
        {trimmedEmail.length > 0 && !emailValid ? (
          <Text style={styles.fieldHintError}>Enter a valid email address.</Text>
        ) : null}
      </View>

      {showHiddenCheckbox ? (
        <TouchableOpacity
          style={styles.checkboxRow}
          onPress={() => setIsHiddenUser((v) => !v)}
          disabled={saving}
          activeOpacity={0.7}
          accessibilityRole="checkbox"
          accessibilityState={{ checked: isHiddenUser }}
          {...(Platform.OS === 'web' ? { dataSet: { createUserHiddenCheckboxRow: 'true' } } : {})}
        >
          <Text
            style={styles.checkboxMark}
            {...(Platform.OS === 'web'
              ? { dataSet: { createUserHiddenCheckboxMark: 'true' } }
              : {})}
          >
            {isHiddenUser ? '☑' : '☐'}
          </Text>
          <Text
            style={styles.checkboxLabel}
            {...(Platform.OS === 'web'
              ? { dataSet: { createUserHiddenCheckboxLabel: 'true' } }
              : {})}
          >
            isHiddenUser
          </Text>
        </TouchableOpacity>
      ) : null}

      <View style={styles.buttonContainer}>
        <View
          style={styles.resetBtnShell}
          {...(Platform.OS === 'web' ? { dataSet: { createUserResetBtn: 'true' } } : {})}
        >
          <TouchableOpacity
            style={styles.resetBtnInner}
            onPress={handleReset}
            disabled={saving}
            activeOpacity={0.85}
          >
            <Text style={styles.resetBtnText}>Reset</Text>
          </TouchableOpacity>
        </View>
        <View
          style={[styles.createBtnShell, !canCreate && styles.createBtnShellDisabled]}
          {...(Platform.OS === 'web'
            ? {
                dataSet: {
                  createUserCreateBtn: 'true',
                  createUserCreateBtnDisabled: !canCreate ? 'true' : 'false',
                },
              }
            : {})}
        >
          <TouchableOpacity
            style={[styles.createBtnInner, !canCreate && styles.createBtnDisabled]}
            onPress={() => void handleCreate()}
            disabled={!canCreate}
            activeOpacity={0.85}
          >
            {saving ? (
              <ActivityIndicator color="#000000" size="small" />
            ) : (
              <Text style={styles.createBtnText}>Create</Text>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </>
  );

  const renderEditTab = () => (
    <>
      <View style={styles.fieldContainer}>
        <Text style={styles.label}>Student</Text>
        <TouchableOpacity
          style={styles.pickTrigger}
          onPress={() => setShowStudentDropdown(true)}
          disabled={loadingStudents || saving}
          {...(Platform.OS === 'web' ? { dataSet: { createUserStudentTrigger: 'true' } } : {})}
        >
          {loadingStudents ? (
            <ActivityIndicator size="small" color={theme.colors.primary || '#d4af37'} />
          ) : (
            <>
              <Text
                style={[
                  styles.pickTriggerText,
                  !selectedStudentId && styles.pickTriggerPlaceholder,
                ]}
                numberOfLines={1}
              >
                {selectedStudent
                  ? `${studentDisplayName(selectedStudent)} (${selectedStudent.username || '—'})`
                  : students.length === 0
                    ? 'No students available'
                    : 'Select student'}
              </Text>
              <Text style={styles.pickTriggerArrow}>▼</Text>
            </>
          )}
        </TouchableOpacity>
      </View>

      {selectedStudent ? (
        <>
          <View style={styles.readOnlyBlock}>
            <Text style={styles.readOnlyLabel}>Username</Text>
            <Text
              style={styles.readOnlyValue}
              {...(Platform.OS === 'web' ? { dataSet: { createUserEditUsername: 'true' } } : {})}
            >
              {selectedStudent.username || '—'}
            </Text>
          </View>

          <View style={styles.fieldContainer}>
            <Text style={styles.label}>Email</Text>
            <TextInput
              style={[
                styles.input,
                trimmedEditEmail.length > 0 && !editEmailValid && styles.inputInvalid,
              ]}
              value={editEmail}
              onChangeText={setEditEmail}
              placeholder="name@example.com"
              placeholderTextColor={
                theme.isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)'
              }
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              editable={!saving}
              {...(Platform.OS === 'web' ? { dataSet: { createUserEditEmailInput: 'true' } } : {})}
            />
            {trimmedEditEmail.length > 0 && !editEmailValid ? (
              <Text style={styles.fieldHintError}>Enter a valid email address.</Text>
            ) : null}
          </View>
        </>
      ) : null}

      <View style={styles.buttonContainer}>
        <View
          style={styles.resetBtnShell}
          {...(Platform.OS === 'web' ? { dataSet: { createUserResetBtn: 'true' } } : {})}
        >
          <TouchableOpacity
            style={styles.resetBtnInner}
            onPress={handleReset}
            disabled={saving}
            activeOpacity={0.85}
          >
            <Text style={styles.resetBtnText}>Reset</Text>
          </TouchableOpacity>
        </View>
        <View
          style={[styles.updateBtnShell, !canUpdateEdit && styles.updateBtnShellDisabled]}
          {...(Platform.OS === 'web'
            ? {
                dataSet: {
                  createUserUpdateBtn: 'true',
                  createUserUpdateBtnDisabled: !canUpdateEdit ? 'true' : 'false',
                },
              }
            : {})}
        >
          <TouchableOpacity
            style={[styles.updateBtnInner, !canUpdateEdit && styles.updateBtnDisabled]}
            onPress={() => void handleUpdateEmail()}
            disabled={!canUpdateEdit}
            activeOpacity={0.85}
          >
            {saving ? (
              <ActivityIndicator color="#000000" size="small" />
            ) : (
              <Text style={styles.updateBtnText}>Update</Text>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </>
  );

  if (currentUser && !allowed) {
    return (
      <View style={styles.container}>
        <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
        <View style={styles.header}>
          <View style={styles.hamburgerContainer}>
            <HamburgerMenu navigation={navigation} />
          </View>
          <View style={styles.breadcrumbContainer}>
            <Text style={styles.breadcrumb}>{PAGE_TITLE}</Text>
          </View>
          <View style={styles.profileIconContainer}>
            <ProfileIcon navigation={navigation} user={currentUser} />
          </View>
        </View>
        <View style={styles.deniedWrap}>
          <Text style={styles.deniedText}>{PAGE_TITLE} is available to super users only.</Text>
        </View>
        <Stopwatch />
      </View>
    );
  }

  return (
    <View
      style={styles.container}
      data-screen="create-user"
      data-theme={theme.isDarkMode ? 'dark' : 'light'}
      {...(Platform.OS === 'web' ? { dataSet: { createUserScreen: 'true' } } : {})}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />

      <View style={styles.header}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>{PAGE_TITLE}</Text>
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
        <View style={styles.titleSection}>
          <Text style={styles.title}>{PAGE_TITLE}</Text>
          <Text style={styles.subtitle}>
            {builderTab === 'create'
              ? 'Add a new student with Beginner grade.'
              : 'Change a student email and send them a password reset link.'}
          </Text>
        </View>

        <View style={styles.pageTabRow}>
          <TouchableOpacity
            style={[styles.pageTab, builderTab === 'create' && styles.pageTabOn]}
            onPress={() => switchBuilderTab('create')}
            disabled={saving}
          >
            <Text style={[styles.pageTabText, builderTab === 'create' && styles.pageTabTextOn]}>
              Create User
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.pageTab, builderTab === 'edit' && styles.pageTabOn]}
            onPress={() => switchBuilderTab('edit')}
            disabled={saving}
          >
            <Text style={[styles.pageTabText, builderTab === 'edit' && styles.pageTabTextOn]}>
              Edit User
            </Text>
          </TouchableOpacity>
        </View>

        {renderFeedbackToast()}

        {builderTab === 'create' ? renderCreateTab() : renderEditTab()}
      </ScrollView>

      <Modal
        visible={showStudentDropdown}
        transparent
        animationType="fade"
        onRequestClose={() => setShowStudentDropdown(false)}
      >
        <View style={styles.modalRoot}>
          <Pressable style={styles.modalBackdrop} onPress={() => setShowStudentDropdown(false)} />
          <View
            style={styles.modalSheet}
            {...(Platform.OS === 'web' ? { dataSet: { createUserStudentDropdown: 'true' } } : {})}
          >
            <Text style={styles.modalTitle}>Select student</Text>
            <ScrollView style={styles.modalScroll} keyboardShouldPersistTaps="handled">
              {students.map((student) => (
                <TouchableOpacity
                  key={student.id}
                  style={[
                    styles.modalRow,
                    Number(selectedStudentId) === Number(student.id) && styles.modalRowSelected,
                  ]}
                  onPress={() => handleSelectStudent(student)}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { createUserStudentDropdownRow: 'true' } }
                    : {})}
                >
                  <Text style={styles.modalRowTitle}>{studentDisplayName(student)}</Text>
                  <Text style={styles.modalRowSub}>
                    {student.username || '—'}
                    {student.student_grade ? ` · ${student.student_grade}` : ''}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      <Stopwatch />
    </View>
  );
}

const getStyles = (theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 20,
      paddingHorizontal: 20,
      backgroundColor: 'transparent',
    },
    hamburgerContainer: {
      marginRight: 15,
      backgroundColor: 'transparent',
    },
    breadcrumbContainer: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    breadcrumb: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.heading,
      backgroundColor: 'transparent',
    },
    profileIconContainer: {
      backgroundColor: 'transparent',
    },
    content: {
      flex: 1,
    },
    contentContainer: {
      padding: 20,
      paddingBottom: 40,
      maxWidth: 640,
      alignSelf: 'center',
      width: '100%',
    },
    titleSection: {
      marginBottom: 24,
      alignItems: 'center',
    },
    title: {
      fontSize: 24,
      fontWeight: '700',
      color: theme.colors.heading,
      marginBottom: 8,
      backgroundColor: 'transparent',
    },
    subtitle: {
      fontSize: 14,
      color: theme.colors.text,
      opacity: 0.75,
      textAlign: 'center',
      lineHeight: 20,
      backgroundColor: 'transparent',
    },
    pageTabRow: {
      flexDirection: 'row',
      gap: 8,
      marginBottom: 20,
      alignSelf: 'stretch',
    },
    pageTab: {
      flex: 1,
      paddingVertical: 12,
      borderRadius: 8,
      alignItems: 'center',
      backgroundColor: theme.isDarkMode ? '#3a3a3a' : '#E8E8E8',
    },
    pageTabOn: {
      backgroundColor: theme.colors.primary || '#d4af37',
    },
    pageTabText: {
      fontSize: 16,
      fontWeight: '700',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    pageTabTextOn: {
      color: '#000',
      backgroundColor: 'transparent',
    },
    fieldContainer: {
      marginBottom: 20,
    },
    label: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 10,
      backgroundColor: 'transparent',
    },
    input: {
      paddingVertical: 14,
      paddingHorizontal: 14,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      fontSize: 16,
      color: theme.colors.text,
      backgroundColor: theme.isDarkMode
        ? theme.colors.inputBackground || theme.colors.surface
        : LIGHT_CREAM,
    },
    inputInvalid: {
      borderColor: '#c62828',
    },
    fieldHintError: {
      marginTop: 6,
      fontSize: 13,
      color: '#c62828',
      backgroundColor: 'transparent',
    },
    pickTrigger: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: 14,
      paddingHorizontal: 14,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      backgroundColor: theme.isDarkMode
        ? theme.colors.inputBackground || theme.colors.surface
        : LIGHT_CREAM,
    },
    pickTriggerText: {
      flex: 1,
      fontSize: 16,
      color: theme.colors.text,
      marginRight: 8,
      backgroundColor: 'transparent',
    },
    pickTriggerPlaceholder: {
      opacity: 0.55,
    },
    pickTriggerArrow: {
      fontSize: 12,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    readOnlyBlock: {
      marginBottom: 20,
      backgroundColor: 'transparent',
    },
    readOnlyLabel: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 8,
      backgroundColor: 'transparent',
    },
    readOnlyValue: {
      fontSize: 16,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    checkboxRow: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 24,
      paddingVertical: 4,
      backgroundColor: 'transparent',
    },
    checkboxMark: {
      fontSize: 20,
      marginRight: 10,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    checkboxLabel: {
      fontSize: 16,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    feedbackToast: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      paddingVertical: 12,
      paddingHorizontal: 12,
      borderRadius: 8,
      marginBottom: 20,
      width: '100%',
    },
    feedbackToastSuccess: {
      backgroundColor: '#2e7d32',
    },
    feedbackToastError: {
      backgroundColor: '#c62828',
    },
    feedbackToastMessage: {
      flex: 1,
      color: '#ffffff',
      fontSize: 15,
      lineHeight: 20,
      marginRight: 8,
      paddingRight: 4,
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
    feedbackToastClose: {
      padding: 4,
      marginTop: -2,
      backgroundColor: 'transparent',
    },
    feedbackToastCloseText: {
      color: '#ffffff',
      fontSize: 18,
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
    buttonContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginTop: 10,
      gap: 15,
    },
    resetBtnShell: {
      flex: 1,
      borderRadius: 8,
      overflow: 'hidden',
      backgroundColor: '#999999',
    },
    resetBtnInner: {
      padding: 15,
      alignItems: 'center',
      backgroundColor: '#999999',
    },
    resetBtnText: {
      color: '#FFFFFF',
      fontSize: 16,
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    createBtnShell: {
      flex: 1,
      borderRadius: 8,
      overflow: 'hidden',
      backgroundColor: theme.colors.primary || '#d4af37',
    },
    createBtnShellDisabled: {
      opacity: 0.55,
    },
    createBtnInner: {
      padding: 15,
      alignItems: 'center',
      backgroundColor: theme.colors.primary || '#d4af37',
    },
    createBtnDisabled: {
      opacity: 0.7,
    },
    createBtnText: {
      color: '#000000',
      fontSize: 16,
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    updateBtnShell: {
      flex: 1,
      borderRadius: 8,
      overflow: 'hidden',
      backgroundColor: theme.colors.primary || '#d4af37',
    },
    updateBtnShellDisabled: {
      opacity: 0.55,
    },
    updateBtnInner: {
      padding: 15,
      alignItems: 'center',
      backgroundColor: theme.colors.primary || '#d4af37',
    },
    updateBtnDisabled: {
      opacity: 0.7,
    },
    updateBtnText: {
      color: '#000000',
      fontSize: 16,
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    deniedWrap: {
      flex: 1,
      justifyContent: 'center',
      padding: 32,
    },
    deniedText: {
      fontSize: 16,
      textAlign: 'center',
      color: theme.colors.text,
      lineHeight: 24,
      backgroundColor: 'transparent',
    },
    modalRoot: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
    },
    modalBackdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0,0,0,0.45)',
    },
    modalSheet: {
      width: '100%',
      maxWidth: 420,
      maxHeight: '70%',
      borderRadius: 12,
      padding: 16,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    modalTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: theme.colors.heading,
      marginBottom: 12,
      backgroundColor: 'transparent',
    },
    modalScroll: {
      maxHeight: 360,
    },
    modalRow: {
      paddingVertical: 12,
      paddingHorizontal: 10,
      borderRadius: 8,
      marginBottom: 6,
      backgroundColor: 'transparent',
    },
    modalRowSelected: {
      backgroundColor: theme.isDarkMode ? 'rgba(212,175,55,0.18)' : 'rgba(212,175,55,0.22)',
    },
    modalRowTitle: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    modalRowSub: {
      fontSize: 13,
      color: theme.colors.text,
      opacity: 0.75,
      marginTop: 2,
      backgroundColor: 'transparent',
    },
  });
